import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Phone, Upload, CheckCircle2, ShieldCheck, Lock, Download, Trash2, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/store";
import { formatApiErrorDetail } from "@/lib/api";
import KycBadge from "@/components/KycBadge";
import { Stars } from "@/components/Stars";

export default function Profile() {
  const { user, setUser, refreshUser, t } = useApp();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [rep, setRep] = useState({ avg: null, count: 0, reviews: [] });
  const [kycConsent, setKycConsent] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { if (user) setPhone(user.phone || ""); }, [user]);
  useEffect(() => { api.get("/my/listings").then(({ data }) => setMyListings(data)).catch(() => {}); }, []);
  useEffect(() => { if (user) api.get(`/users/${user.user_id}/rating`).then(({ data }) => setRep(data)).catch(() => {}); }, [user]);

  const resendVerification = async () => {
    try { await api.post("/auth/resend-verification"); toast.success(t("verify_resent")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const changePassword = async () => {
    if (pw.next.length < 8) { toast.error(t("reset_min")); return; }
    if (pw.next !== pw.confirm) { toast.error(t("reset_mismatch")); return; }
    setPwBusy(true);
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); toast.success(t("change_pw_done")); setPw({ current: "", next: "", confirm: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };
  const sendOtp = async () => {
    try {
      const { data } = await api.post("/phone/send-otp", { phone_number: phone });
      setOtpSent(true);
      if (data.mode === "mock") toast.info(`Dev mode: use code ${data.dev_code}`);
      else toast.success("Code sent via SMS");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const verifyOtp = async () => {
    try {
      const { data } = await api.post("/phone/verify-otp", { phone_number: phone, code: otp });
      if (data.valid) { toast.success("Phone verified!"); await refreshUser(); setOtpSent(false); }
      else toast.error("Invalid code");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const uploadKyc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", "id_document");
    try {
      await api.post("/kyc/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded. Status: Pending Approval");
      await refreshUser();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `rentglobe-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Your data has been exported");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const deleteAccount = async () => {
    try {
      await api.post("/me/delete", { confirm: true, password: delPassword || undefined });
      toast.success("Your account and personal data have been deleted");
      setUser(false);
      window.location.href = "/";
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[#002FA7] font-display text-2xl font-extrabold text-white">{(user.name || "?")[0]}</div>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight" data-testid="profile-name">{user.name}</h1>
            <div className="text-sm text-gray-500" data-testid="profile-email">{user.email}</div>
            <div className="mt-1 text-xs font-bold uppercase tracking-widest text-gray-400">
              {user.person_type === "business" ? t("person_business") : t("person_individual")} · {user.country} · {user.data_region}
            </div>
          </div>
        </div>
        <KycBadge status={user.kyc_status} />
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-2">
        {/* KYC upload */}
        <div className="rounded-2xl border border-gray-200 p-6">
          <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><ShieldCheck size={18} className="text-[#002FA7]" /> Identity Verification (KYC)</div>
          <p className="text-sm text-gray-500" data-testid="kyc-explainer">Upload a government-issued ID. Our team manually checks that the document looks genuine and that the name matches your account, then grants the <b>Verified ID</b> badge. The badge only confirms an ID document was reviewed — RentGlobe does not verify listings, items or rental agreements. Your document is stored encrypted and seen only by our compliance team.</p>
          <div className="mt-4">
            {user.kyc_document ? (
              <div className="rounded-xl bg-gray-50 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700"><CheckCircle2 size={16} className="text-[#155724]" /> {user.kyc_document.original_filename}</div>
                <div className="mt-2"><KycBadge status={user.kyc_status} /></div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No document submitted yet.</p>
            )}
            <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={uploadKyc} className="hidden" data-testid="kyc-file-input" />
            <label className="mt-4 flex items-start gap-2 text-xs text-gray-600">
              <input data-testid="kyc-consent" type="checkbox" checked={kycConsent} onChange={(e) => setKycConsent(e.target.checked)} className="mt-0.5" />
              I explicitly consent to RentGlobe processing and storing this identity document for verification purposes, under LGPD (BR) & Privacy Act (AU).
            </label>
            <button data-testid="kyc-upload-btn" disabled={!kycConsent} onClick={() => fileRef.current?.click()}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#002FA7] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#002280] disabled:cursor-not-allowed disabled:opacity-50">
              <Upload size={16} /> {user.kyc_document ? "Replace document" : "Upload document"}
            </button>
          </div>
          <div className="mt-4 flex items-center gap-1.5 text-xs text-gray-400"><Lock size={12} /> Protected under LGPD (BR) & Privacy Act (AU)</div>
        </div>

        {/* Email verification (primary) */}
        <div className="rounded-2xl border border-gray-200 p-6" data-testid="email-verification-card">
          <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><Mail size={18} className="text-[#002FA7]" /> Email Verification</div>
          {user.email_verified ? (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#D4EDDA] px-4 py-2 text-sm font-bold text-[#155724]" data-testid="email-verified"><CheckCircle2 size={16} /> {user.email} · {t("email_verified")}</div>
          ) : (
            <div className="mt-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-2 text-sm font-bold text-amber-800" data-testid="email-not-verified">{t("email_not_verified")}</div>
              <button data-testid="profile-resend-verification" onClick={resendVerification} className="mt-3 w-full rounded-full border border-[#002FA7] py-2.5 text-sm font-bold text-[#002FA7] transition-colors hover:bg-[#002FA7] hover:text-white">{t("verify_resend")}</button>
            </div>
          )}
        </div>

        {/* Change password */}
        {user.has_password && (
          <div className="rounded-2xl border border-gray-200 p-6" data-testid="change-password-card">
            <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><Lock size={18} className="text-[#002FA7]" /> {t("change_pw_title")}</div>
            <p className="text-sm text-gray-500">{t("change_pw_sub")}</p>
            <div className="mt-4 space-y-2">
              <input data-testid="current-password" type="password" placeholder={t("current_pw")} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#002FA7]" />
              <input data-testid="new-password" type="password" placeholder={t("reset_new")} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#002FA7]" />
              <input data-testid="confirm-password" type="password" placeholder={t("reset_confirm")} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-[#002FA7]" />
              <button data-testid="change-password-btn" disabled={pwBusy} onClick={changePassword} className="w-full rounded-full bg-[#002FA7] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#002280] disabled:opacity-60">{t("change_pw_btn")}</button>
            </div>
          </div>
        )}

        {/* Phone verification (optional) */}
        <div className="rounded-2xl border border-gray-200 p-6">
          <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><Phone size={18} className="text-[#002FA7]" /> Phone Verification <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-500">Optional</span></div>
          <p className="text-sm text-gray-500">{t("phone_optional_hint")}</p>
          {user.phone_verified ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#D4EDDA] px-4 py-2 text-sm font-bold text-[#155724]" data-testid="phone-verified"><CheckCircle2 size={16} /> {user.phone} · {t("phone_verified")}</div>
          ) : (
            <div className="mt-4 space-y-3">
              <input data-testid="phone-input" placeholder="+55 11 99999-9999" value={phone} onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
              {!otpSent ? (
                <button data-testid="send-otp-btn" onClick={sendOtp} className="w-full rounded-full border border-[#002FA7] py-2.5 text-sm font-bold text-[#002FA7] transition-colors hover:bg-[#002FA7] hover:text-white">Send code</button>
              ) : (
                <div className="space-y-3">
                  <input data-testid="otp-input" placeholder="Enter 6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20" />
                  <button data-testid="verify-otp-btn" onClick={verifyOtp} className="w-full rounded-full bg-[#002FA7] py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#002280]">Verify</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8" data-testid="profile-reputation">
        <div className="mb-4 flex items-center gap-3">
          <h2 className="font-display text-xl font-bold">{t("reviews")}</h2>
          {rep.avg != null ? (
            <div className="flex items-center gap-1.5"><Stars value={rep.avg} /><span className="text-sm font-bold text-gray-700">{rep.avg}</span><span className="text-sm text-gray-400">({rep.count})</span></div>
          ) : <span className="text-sm text-gray-400" data-testid="profile-reputation-empty">{t("no_reviews")}</span>}
        </div>
        {rep.reviews.length > 0 && (
          <div className="space-y-3" data-testid="profile-reviews-list">
            {rep.reviews.map((r) => (
              <div key={r.review_id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900">{r.reviewer_name}<span className="ml-2 text-xs font-normal uppercase tracking-widest text-gray-400">as {r.role}</span></span>
                  <Stars value={r.rating} size={14} />
                </div>
                {r.comment && <p className="mt-1 text-sm text-gray-600">{r.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8">
        <h2 className="mb-4 font-display text-xl font-bold">My listings ({myListings.length})</h2>
        {myListings.length === 0 ? (
          <p className="text-gray-400" data-testid="my-listings-empty">You haven't listed anything yet.</p>
        ) : (
          <div className="grid gap-3" data-testid="my-listings">
            {myListings.map((l) => (
              <div key={l.listing_id} className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
                <img src={l.image_url} alt="" className="h-12 w-12 rounded-lg object-cover" />
                <div className="flex-1"><div className="font-semibold">{l.title}</div><div className="text-xs text-gray-400">{l.price_per_day} {l.currency} {t("per_day")}</div></div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10 rounded-2xl border border-gray-200 p-6" data-testid="privacy-card">
        <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold"><Lock size={18} className="text-[#002FA7]" /> Privacy & your data</div>
        <p className="text-sm text-gray-500">Under LGPD (Brazil) and the Privacy Act (Australia) you can access a copy of your data at any time and request its deletion.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button data-testid="export-data-btn" onClick={exportData} className="inline-flex items-center gap-2 rounded-full border border-[#002FA7] px-5 py-2.5 text-sm font-bold text-[#002FA7] transition-colors hover:bg-[#002FA7] hover:text-white">
            <Download size={16} /> Export my data
          </button>
          {!confirmDelete ? (
            <button data-testid="delete-account-btn" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-2 rounded-full border border-[#FF3B30] px-5 py-2.5 text-sm font-bold text-[#FF3B30] transition-colors hover:bg-[#FF3B30]/5">
              <Trash2 size={16} /> Delete my account
            </button>
          ) : (
            <div className="w-full rounded-xl border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4" data-testid="delete-confirm-box">
              <p className="text-sm font-semibold text-[#FF3B30]">This permanently deletes your account, listings and personal data. This cannot be undone.</p>
              {user.auth_provider === "password" && (
                <input data-testid="delete-password" type="password" placeholder="Confirm your password" value={delPassword} onChange={(e) => setDelPassword(e.target.value)}
                  className="mt-3 w-full max-w-xs rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#FF3B30]" />
              )}
              <div className="mt-3 flex gap-2">
                <button data-testid="delete-confirm-btn" onClick={deleteAccount} className="rounded-full bg-[#FF3B30] px-5 py-2 text-sm font-bold text-white">Permanently delete</button>
                <button data-testid="delete-cancel-btn" onClick={() => { setConfirmDelete(false); setDelPassword(""); }} className="rounded-full border border-gray-300 px-5 py-2 text-sm font-bold text-gray-600">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
